import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, LabelList, Legend,
} from "recharts";
import { getAnalyticsSummary, getScanGraph, listQRCodes } from "../services/api";

const COLORS = [
  "#0046FF", "#00B8D9", "#1E9E5A", "#F59E0B", "#8B5CF6",
  "#EC4899", "#F97316", "#14B8A6", "#6366F1", "#84CC16",
];

const PERIODS = [
  { value: "daily", label: "Today", phrase: "today" },
  { value: "weekly", label: "This Week", phrase: "this week" },
  { value: "monthly", label: "This Month", phrase: "this month" },
  { value: "custom", label: "Custom", phrase: "in the selected range" },
];

const Svg = ({ size = 16, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

const QrIcon = ({ size }) => (
  <Svg size={size}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3h-3zM20 14v.01M14 20h3M20 17v4" />
  </Svg>
);

const ScanIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3" />
    <path d="M4 12h16" />
  </Svg>
);

const TrendIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M3 17l6-6 4 4 8-8" />
    <path d="M14 7h7v7" />
  </Svg>
);

const ChartIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </Svg>
);

function BarTooltip({ active, payload, totalScans }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  const share = totalScans ? Math.round((row.scans / totalScans) * 100) : 0;

  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{row.name}</div>
      <div className="chart-tooltip-row">
        <span className="chart-tooltip-dot" style={{ background: row.color }} />
        <span className="chart-tooltip-name">Scans</span>
        <span className="chart-tooltip-value">{row.scans}</span>
      </div>
      <div className="chart-tooltip-row">
        <span className="chart-tooltip-dot" style={{ background: "#9CA3AF" }} />
        <span className="chart-tooltip-name">Share of total</span>
        <span className="chart-tooltip-value">{share}%</span>
      </div>
    </div>
  );
}

const CalendarIcon = ({ size }) => (
  <Svg size={size}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M16 3v4M8 3v4M3 10h18" />
  </Svg>
);

const ArrowDownIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M12 5v14M6 13l6 6 6-6" />
  </Svg>
);

const ArrowUpIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M12 19V5M6 11l6-6 6 6" />
  </Svg>
);

const fmtDate = (d) =>
  d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

const fromInput = (value) => new Date(`${value}T00:00:00`);

/* Local YYYY-MM-DD (not toISOString, which shifts to UTC and can land on
   the wrong day depending on the user's timezone/time of day). */
const toInputDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/* Computes the actual start/end dates for a period, in the browser's local
   timezone, so "Today"/"This Week"/"This Month" always mean what the user
   sees on screen — regardless of how the backend would otherwise guess it. */
function getPeriodRange(period, custom) {
  const now = new Date();

  if (period === "daily") {
    const today = toInputDate(now);
    return { start: today, end: today };
  }

  if (period === "weekly") {
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: toInputDate(monday), end: toInputDate(sunday) };
  }

  if (period === "monthly") {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start: toInputDate(first), end: toInputDate(last) };
  }

  // custom
  if (custom && custom.start && custom.end) {
    return { start: custom.start, end: custom.end };
  }
  return null;
}

/* Human-readable date range for the active filter. Returns null if not ready. */
function getRangeLabel(period, applied) {
  if (period === "daily") return fmtDate(new Date());

  if (period === "weekly") {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return `${fmtDate(monday)} – ${fmtDate(sunday)}`;
  }

  if (period === "monthly") {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return `${fmtDate(first)} – ${fmtDate(last)}`;
  }

  if (applied && applied.start && applied.end) {
    return applied.start === applied.end
      ? fmtDate(fromInput(applied.start))
      : `${fmtDate(fromInput(applied.start))} – ${fmtDate(fromInput(applied.end))}`;
  }
  return null;
}

const ChevronIcon = ({ size }) => (
  <Svg size={size}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
);

const XIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Svg>
);

const SearchIcon = ({ size }) => (
  <Svg size={size}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Svg>
);

const CheckIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M5 12.5 10 17.5 19 7.5" />
  </Svg>
);

/* Multi-select dropdown with search and removable chips */
function MultiSelect({ options, value, onChange, placeholder = "All QR Codes" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
    if (!open) setSearch("");
  }, [open]);

  const selected = options.filter((o) => value.includes(o.id));
  const filtered = options.filter((o) =>
    (o.title || "").toLowerCase().includes(search.trim().toLowerCase())
  );

  const toggle = (id) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  const remove = (e, id) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== id));
  };

  const MAX_CHIPS = 2;

  return (
    <div className="multi-select" ref={rootRef}>
      <div
        className={`ms-control${open ? " open" : ""}`}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
      >
        <div className="ms-values">
          {selected.length === 0 && <span className="ms-placeholder">{placeholder}</span>}
          {selected.slice(0, MAX_CHIPS).map((o) => (
            <span key={o.id} className="ms-chip">
              <span className="ms-chip-label">{o.title}</span>
              <button
                type="button"
                className="ms-chip-x"
                aria-label={`Remove ${o.title}`}
                onClick={(e) => remove(e, o.id)}
              >
                <XIcon size={12} />
              </button>
            </span>
          ))}
          {selected.length > MAX_CHIPS && (
            <span className="ms-chip ms-chip-more">+{selected.length - MAX_CHIPS}</span>
          )}
        </div>

        {selected.length > 0 && (
          <button
            type="button"
            className="ms-clear"
            aria-label="Clear all"
            title="Clear all"
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
          >
            <XIcon size={14} />
          </button>
        )}
        <span className="ms-chevron"><ChevronIcon size={16} /></span>
      </div>

      {open && (
        <div className="ms-menu">
          <div className="ms-search">
            <SearchIcon size={15} />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search QR codes"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button type="button" aria-label="Clear search" onClick={() => setSearch("")}>
                <XIcon size={13} />
              </button>
            )}
          </div>

          <div className="ms-actions">
            <button
              type="button"
              onClick={() => onChange([...new Set([...value, ...filtered.map((o) => o.id)])])}
              disabled={filtered.length === 0}
            >
              Select {search ? "results" : "all"}
            </button>
            <button type="button" onClick={() => onChange([])} disabled={value.length === 0}>
              Clear
            </button>
          </div>

          <div className="ms-list" role="listbox" aria-multiselectable="true">
            {filtered.length === 0 ? (
              <div className="ms-empty">No QR codes found</div>
            ) : (
              filtered.map((o) => {
                const checked = value.includes(o.id);
                return (
                  <div
                    key={o.id}
                    role="option"
                    aria-selected={checked}
                    className={`ms-option${checked ? " selected" : ""}`}
                    onClick={() => toggle(o.id)}
                  >
                    <span className="ms-check">{checked && <CheckIcon size={12} />}</span>
                    <span className="ms-option-label">{o.title}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* Year multi-select dropdown that closes when clicking outside */
function YearDropdown({ selectedYears, setSelectedYears }) {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        dropdownRef.current.open = false;
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="toolbar-field">
      <label className="form-label">Year Filter</label>

      <div className="year-dropdown">
        <details ref={dropdownRef}>
          <summary>
            {selectedYears.length > 0
              ? `${selectedYears.length} year(s) selected`
              : "Select years"}
          </summary>

          <div className="year-dropdown-options">
            {Array.from(
              { length: 8 },
              (_, index) => new Date().getFullYear() - index
            ).map((year) => {
              const yearValue = String(year);

              return (
                <label key={year} className="year-dropdown-option">
                  <input
                    type="checkbox"
                    checked={selectedYears.includes(yearValue)}
                    onChange={(e) => {
                      setSelectedYears((prev) =>
                        e.target.checked
                          ? [...prev, yearValue]
                          : prev.filter((item) => item !== yearValue)
                      );
                    }}
                  />

                  <span>{year}</span>
                </label>
              );
            })}
          </div>
        </details>
      </div>
    </div>
  );
}

const truncate = (text, max = 18) =>
  text && text.length > max ? `${text.slice(0, max - 1)}…` : text;

export default function Analytics() {
  const [summary, setSummary] = useState({ total_qr_codes: 0, total_scans: 0 });
  const [codes, setCodes] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [period, setPeriod] = useState("daily");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [graphData, setGraphData] = useState([]);
  const [series, setSeries] = useState({});
  const [loading, setLoading] = useState(true);
  const [showZero, setShowZero] = useState(false);
  const [viewMode, setViewMode] = useState("qr");
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [selectedYears, setSelectedYears] = useState([String(new Date().getFullYear())]);
  const [appliedRange, setAppliedRange] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    getAnalyticsSummary().then(({ data }) => setSummary(data));
    listQRCodes().then(({ data }) => setCodes(data.results || data));
  }, []);

  const loadGraph = () => {
    if (!["month", "year"].includes(viewMode) && period === "custom" && (!start || !end)) {
      // Nothing to search yet: wait for the user to pick both dates and press Apply.
      setGraphData([]);
      setSeries({});
      setAppliedRange(null);
      setLoading(false);
      return;
    }

    // Always resolve an explicit start/end for whichever period is active,
    // so "Today"/"This Week"/"This Month" are unambiguous to the backend
    // instead of relying on it to compute the same boundary independently.
    const range = viewMode === "month"
      ? { start: `${selectedYear}-01-01`, end: `${selectedYear}-12-31` }
      : viewMode === "year"
        ? (selectedYears.length
            ? { start: `${Math.min(...selectedYears.map(Number))}-01-01`, end: `${Math.max(...selectedYears.map(Number))}-12-31` }
            : null)
        : getPeriodRange(period, period === "custom" ? { start, end } : null);
    if (!range) {
      setGraphData([]);
      setSeries({});
      setLoading(false);
      return;
    }

    setLoading(true);
    const params = ["month", "year"].includes(viewMode)
      ? { period: "custom", start: range.start, end: range.end }
      : { period, start: range.start, end: range.end };
    if (period === "custom" || ["month", "year"].includes(viewMode)) {
      setAppliedRange({ start: range.start, end: range.end });
    }

    getScanGraph(params)
      .then(({ data }) => {
        setGraphData(data.data || []);
        setSeries(data.series || {});
      })
      .finally(() => setLoading(false));
  };

  useEffect(loadGraph, [period, viewMode, selectedYear, selectedYears]); // eslint-disable-line react-hooks/exhaustive-deps

  /* One bar per QR code: sum each code's scans across the whole period,
     then sort highest first. */
  const { barData, periodTotal } = useMemo(() => {
    const selectedNames = new Set(
      codes.filter((qr) => selectedIds.includes(qr.id)).map((qr) => qr.title)
    );
    const hasSelection = selectedIds.length > 0;

    // Colour index is taken before filtering so each QR keeps its colour when the selection changes.
    const rows = Object.keys(series)
      .map((key, i) => ({ key, i }))
      .filter(({ key }) => !hasSelection || selectedNames.has(series[key]))
      .map(({ key, i }) => ({
        key,
        color: COLORS[i % COLORS.length],
        name: series[key],
        scans: graphData.reduce((sum, row) => sum + (Number(row[key]) || 0), 0),
      }));

    // Codes that had no activity may be missing from `series`; add them as zero rows.
    if (showZero) {
      const known = new Set(rows.map((r) => r.name));
      const candidates = hasSelection ? codes.filter((qr) => selectedIds.includes(qr.id)) : codes;
      candidates.forEach((qr) => {
        if (!known.has(qr.title)) {
          rows.push({
            key: `qr-${qr.id}`,
            color: COLORS[rows.length % COLORS.length],
            name: qr.title,
            scans: 0,
          });
        }
      });
    }

    const total = rows.reduce((sum, r) => sum + r.scans, 0);
    const visible = rows.filter((r) => showZero || r.scans > 0).sort((a, b) => (sortOrder === "desc" ? b.scans - a.scans : a.scans - b.scans));
    return { barData: visible, periodTotal: total };
  }, [graphData, series, codes, showZero, selectedIds, sortOrder]);

  /* Date/month chart: aggregate QR scan series by the selected time unit. */
  const dateGraphData = useMemo(() => {
    const selectedNames = new Set(
      codes.filter((qr) => selectedIds.includes(qr.id)).map((qr) => qr.title)
    );
    const hasSelection = selectedIds.length > 0;

    const visibleSeries = Object.keys(series)
      .map((key, index) => ({
        key,
        name: series[key],
        color: COLORS[index % COLORS.length],
      }))
      .filter((item) => !hasSelection || selectedNames.has(item.name));

    const firstRow = graphData[0] || {};
    const possibleDateKeys = ["date", "day", "label", "period", "timestamp", "created_at"];
    const dateKey = possibleDateKeys.find((key) => key in firstRow) ||
      Object.keys(firstRow).find((key) => !Object.keys(series).includes(key));

    const grouped = new Map();
    graphData.forEach((row) => {
      const rawDate = dateKey ? row[dateKey] : "";
      if (!rawDate) return;
      const parsed = new Date(rawDate);
      if (Number.isNaN(parsed.getTime())) return;

      const monthMode = viewMode === "month";
      const yearMode = viewMode === "year";
      const groupKey = yearMode
        ? String(parsed.getFullYear())
        : monthMode
          ? `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`
          : `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}-${String(parsed.getDate()).padStart(2, "0")}`;
      const chartDate = yearMode
        ? String(parsed.getFullYear())
        : monthMode
          ? parsed.toLocaleDateString("en-GB", { month: "short", year: "numeric" })
          : parsed.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

      if (!grouped.has(groupKey)) {
        const initial = { chartDate, _sortKey: groupKey };
        Object.keys(series).forEach((key) => { initial[key] = 0; });
        grouped.set(groupKey, initial);
      }
      const target = grouped.get(groupKey);
      Object.keys(series).forEach((key) => {
        if (hasSelection && !selectedNames.has(series[key])) return;
        target[key] += Number(row[key]) || 0;
      });
    });

    let data = [...grouped.values()].sort((a, b) => a._sortKey.localeCompare(b._sortKey));

    // Date view: skip days with no scans for the visible QR code(s).
    if (viewMode === "date") {
      data = data.filter((row) =>
        visibleSeries.some((item) => (Number(row[item.key]) || 0) > 0)
      );
    }
    return { data, visibleSeries };
  }, [graphData, series, codes, selectedIds, viewMode]);

  const avgScans = summary.total_qr_codes
    ? (summary.total_scans / summary.total_qr_codes).toFixed(1)
    : "0";

  const rangeLabel = viewMode === "month"
    ? selectedYear
    : viewMode === "year"
      ? selectedYears.join(", ")
      : getRangeLabel(period, appliedRange);
  const periodPhrase = PERIODS.find((p) => p.value === period)?.phrase || "";
  const chartMinWidth = Math.max(480, barData.length * 88);

  return (
    <div>
      <div className="page-title">Analytics</div>
      <div className="page-subtitle">Track how your QR codes are performing</div>

      {/* STAT CARDS */}
      <div className="grid grid-3" style={{ marginBottom: 24 }}>
        <div className="card stat-card stat-blue">
          <div className="stat-head"><QrIcon /> Total QR Codes</div>
          <div className="stat-value">{summary.total_qr_codes}</div>
          <div className="stat-foot">Across your account</div>
          <div className="stat-watermark"><QrIcon size={96} /></div>
        </div>

        <div className="card stat-card stat-green">
          <div className="stat-head"><ScanIcon /> Total Scans</div>
          <div className="stat-value">{summary.total_scans}</div>
          <div className="stat-foot">All time</div>
          <div className="stat-watermark"><ScanIcon size={96} /></div>
        </div>

        <div className="card stat-card stat-purple">
          <div className="stat-head"><TrendIcon /> Avg. Scans per QR</div>
          <div className="stat-value">{avgScans}</div>
          <div className="stat-foot">Total scans ÷ QR codes</div>
          <div className="stat-watermark"><TrendIcon size={96} /></div>
        </div>
      </div>

      {/* CHART CARD */}
      <div className="card chart-card">
        <div className="chart-header">
          <div>
            <div className="card-title" style={{ marginBottom: 4 }}>
              <span className="card-title-icon"><ChartIcon size={18} /></span>
              Scans by QR code {periodPhrase}
            </div>
            <div className="chart-subtitle">
              {viewMode === "date"
                ? "Daily scan counts for each QR code, with a separate legend and thin bars."
                : viewMode === "month"
                  ? "Monthly scan totals for each QR code, with a separate legend and thin bars."
                  : `Each bar is one QR code. ${showZero ? "Including" : "QR codes with"} zero scans${showZero ? " are shown." : " are hidden by default."}`}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <label className="toggle-check">
              <input
                type="checkbox"
                checked={showZero}
                onChange={(e) => setShowZero(e.target.checked)}
              />
              Show zero-scan codes
            </label>
            <div className="chart-view-toggle">
              <label htmlFor="analytics-view-select">View</label>
              <select
                id="analytics-view-select"
                className="view-toggle-select"
                value={viewMode}
                onChange={(e) => {
                  const newView = e.target.value;
                  setViewMode(newView);

                  if (newView === "year" && selectedYears.length === 0) {
                    setSelectedYears([String(new Date().getFullYear())]);
                  }
                }}
              >
                <option value="qr">QR Code</option>
                <option value="date">Date</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </div>
          </div>
        </div>

        {/* TOOLBAR */}
        <div className="toolbar">
          <div className="toolbar-field toolbar-field-wide">
            <label className="form-label">QR Codes</label>
            <MultiSelect
              options={codes}
              value={selectedIds}
              onChange={setSelectedIds}
              placeholder="All QR Codes"
            />
          </div>

          {viewMode === "month" ? (
            <div className="toolbar-field">
              <label className="form-label">Year Filter</label>
              <select className="form-input" value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}>
                {Array.from({ length: 8 }, (_, index) => new Date().getFullYear() - index)
                  .map((year) => <option key={year} value={String(year)}>{year}</option>)}
              </select>
            </div>
          ) : viewMode === "year" ? (
            <YearDropdown
              selectedYears={selectedYears}
              setSelectedYears={setSelectedYears}
            />
          ) : (
            <div className="toolbar-field">
              <label className="form-label">Date Filter</label>
              <div className="segmented">
                {PERIODS.map((p) => (
                  <button
                    type="button"
                    key={p.value}
                    className={period === p.value ? "active" : ""}
                    onClick={() => setPeriod(p.value)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!["month", "year"].includes(viewMode) && period === "custom" && (
            <div className="toolbar-range">
              <div className="toolbar-field">
                <label className="form-label">Start</label>
                <input
                  type="date"
                  className="form-input"
                  value={start}
                  max={end || undefined}
                  onChange={(e) => setStart(e.target.value)}
                />
              </div>
              <span className="toolbar-range-sep" aria-hidden="true">→</span>
              <div className="toolbar-field">
                <label className="form-label">End</label>
                <input
                  type="date"
                  className="form-input"
                  value={end}
                  min={start || undefined}
                  onChange={(e) => setEnd(e.target.value)}
                />
              </div>
              <button
                className="btn btn-primary toolbar-apply"
                onClick={loadGraph}
                disabled={!start || !end}
              >
                Apply
              </button>
            </div>
          )}
        </div>

        {/* PERIOD + SORT */}
        <div className="chart-meta">
          <div className="period-pill">
            <CalendarIcon size={14} />
            <span className="period-pill-label">Showing</span>
            <strong>{rangeLabel || "Pick a start and end date"}</strong>
          </div>

          <div className="segmented sort-toggle" role="group" aria-label="Sort order">
            <button
              type="button"
              className={sortOrder === "desc" ? "active" : ""}
              onClick={() => setSortOrder("desc")}
              aria-pressed={sortOrder === "desc"}
            >
              <ArrowDownIcon size={14} /> High to Low
            </button>
            <button
              type="button"
              className={sortOrder === "asc" ? "active" : ""}
              onClick={() => setSortOrder("asc")}
              aria-pressed={sortOrder === "asc"}
            >
              <ArrowUpIcon size={14} /> Low to High
            </button>
          </div>
        </div>

        {/* CHART */}
        {loading ? (
          <div className="chart-state">
            <div className="spinner" />
            Loading chart...
          </div>
        ) : (viewMode !== "qr" ? dateGraphData.data.length === 0 : barData.length === 0) ? (
          <div className="chart-state">
            <span className="chart-state-icon"><ChartIcon size={26} /></span>
            <strong>No scan data yet</strong>
            <span>
              {period === "custom" && !appliedRange
                ? "Pick a start and end date, then press Apply."
                : "Nothing was scanned in this period. Try a wider date range."}
            </span>
          </div>
        ) : (
          <div className="chart-scroll">
            <div
              style={{
                minWidth: viewMode !== "qr"
                  ? Math.max(600, dateGraphData.data.length * 100)
                  : chartMinWidth,
              }}
            >
              <ResponsiveContainer width="100%" height={360}>
                <BarChart
                  data={viewMode !== "qr" ? dateGraphData.data : barData}
                  margin={{ top: 24, right: 16, left: -8, bottom: 0 }}
                  barCategoryGap={viewMode !== "qr" ? "25%" : "22%"}
                  barGap={viewMode !== "qr" ? 2 : 4}
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 5" stroke="#DDE2EC" />

                  <XAxis
                    dataKey={viewMode !== "qr" ? "chartDate" : "name"}
                    tick={{ fontSize: 12, fill: "#6B7280" }}
                    tickFormatter={(value) => viewMode !== "qr" ? value : truncate(value, 14)}
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    dy={8}
                  />
                  <YAxis
                    type="number"
                    allowDecimals={false}
                    domain={[0, (max) => Math.max(5, Math.ceil(max * 1.1))]}
                    tick={{ fontSize: 12, fill: "#6B7280" }}
                    axisLine={false}
                    tickLine={false}
                  />

                  {viewMode !== "qr" ? (
                    <Tooltip />
                  ) : (
                    <Tooltip
                      content={<BarTooltip totalScans={periodTotal} />}
                      cursor={{ fill: "rgba(0,70,255,0.05)" }}
                    />
                  )}

                  {viewMode !== "qr" && (
                    <Legend
                      verticalAlign="top"
                      align="center"
                      height={40}
                      wrapperStyle={{ fontSize: 12, paddingBottom: 10 }}
                    />
                  )}

                  {viewMode !== "qr" ? (
                    dateGraphData.visibleSeries.map((item) => (
                      <Bar
                        key={item.key}
                        dataKey={item.key}
                        name={item.name}
                        fill={item.color}
                        radius={[3, 3, 0, 0]}
                        maxBarSize={14}
                        minPointSize={0}
                      />
                    ))
                  ) : (
                    <Bar
                      dataKey="scans"
                      radius={[8, 8, 0, 0]}
                      maxBarSize={56}
                      minPointSize={4}
                    >
                      {barData.map((row) => (
                        <Cell key={row.key} fill={row.color} />
                      ))}
                      <LabelList
                        dataKey="scans"
                        position="top"
                        style={{ fontSize: 12, fontWeight: 600, fill: "#111827" }}
                      />
                    </Bar>
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}